/**
 * Copyright (C) 2018 Robert Bosch Manufacturing Solutions GmbH, Germany. All rights reserved.
 */

import {
  Location,
  LocationStrategy,
  PathLocationStrategy
} from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BciImprintComponent,
  BciSidebarService,
  LogoutComponent,
  ModalWindowService,
  NavigationService,
  SidebarNavItem
} from '@bci-web-core/core';
import { AdalService } from 'adal-angular4';
import { Observable, Subject, of } from 'rxjs';
import { SystemPrivacyModalComponent } from 'src/app/Components/user/system-privacy-modal/system-privacy-modal.component';
import { ButtonConfig } from 'src/app/models/button.component.model';
import { AuthUserInfo } from 'src/app/models/index';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { CommonService, SiteService } from 'src/app/services/index';
import { AuthGuard, Constants, WarningDialog } from 'src/app/shared/index';
import { environment } from '../environments/environment';
import { ContactUsModalComponent } from './Components/user/contact-us-modal/contact-us-modal.component';
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'bci-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy }
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'BOSCH DEEPSIGHTS';
  sidebarLinks: SidebarNavItem[] = [];
  public sidebarFooterLinks: SidebarNavItem[] = [
    {
      title: 'Logout',
      overlay: {
        component: LogoutComponent,
        config: {
          title: 'Do you want to logout?',
          onLogout: () => this.logout()
        }
      },
      icon: 'Bosch-Ic-user'
    },
    {
      id: 'imprint',
      title: 'Legal',
      icon: 'Bosch-Ic-information',
      items: [
        {
          id: 'usagePolicy',
          title: 'Usage Policy',
          cb: () => this.onPolicy()
        },
        {
          id: 'Copyright',
          title: 'Copyright Information',
          cb: () => this.onAbout()
        }
      ]
    },
    {
      title: 'Contact Us',
      cb: () => this.onContact(),
      icon: 'bosch-ic-call'
    }
  ];
  extendedNavigation = false;
  location: Location;
  userActivity;
  userInactive: Subject<any> = new Subject();
  INACTIVE_CHECK_NUMBER = 6;
  inactiveCount = 0;
  // Get from environment.ts
  MAX_ALLOWED_INACTIVE_MINUTES = 30;
  isDialogOpened = false;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private navigationService: NavigationService,
    private modalWindowService: ModalWindowService,
    private sidebarService: BciSidebarService,
    public auth: AuthGuard,
    private authService: AuthService,
    private commonService: CommonService,
    private elRef: ElementRef,
    private dialog: MatDialog,
    location: Location,
    private adalService: AdalService,
    private siteService: SiteService,
    private dataService: DataService
  ) {
    this.location = location;
    adalService.init(environment.config);
    const userIdleTimeInMinutes = `${environment.userIdleTimeInMinutes}`;
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.MAX_ALLOWED_INACTIVE_MINUTES =
      Number(userIdleTimeInMinutes) * 1000 * 60;
    this.inactiveCount = 0;

    this.startCounter();

    this.userInactive.subscribe(() => {
      // Ignore check if standing at login page
      if (this.router.url !== '/') {
        // After idle time
        let inactiveTime =
          new Date().getTime() -
          Number(localStorage.getItem('lastActivityTime'));
        // Every 1 min, if there is any activity, send heartbeat to server to keep alive
        // This is to avoid difference time count in case user opens multiple tabs
        if (this.inactiveCount === this.INACTIVE_CHECK_NUMBER - 1) {
          // Send heart beat to reset counter of Back End.
          // this.authService.sendHeartBeat().subscribe();
        }
        this.inactiveCount++;

        if (
          currentUser.role.code !== Constants.ACTIVITY.NO_LOGOUT &&
          !currentUser.activities.includes(Constants.ACTIVITY.UNLIMITED_SESSION)
        ) {
          if (
            inactiveTime >= this.MAX_ALLOWED_INACTIVE_MINUTES ||
            localStorage.getItem('needForceLogout') === 'true'
          ) {
            // Alert and force logout
            localStorage.setItem('needForceLogout', 'true');
            this.showDialogAndLogout();
          } else {
            this.startCounter();
          }
        }
      } else {
        // Continue time counter
        this.startCounter();
      }
    });
  }

  showDialogAndLogout() {
    if (this.dialog.openDialogs.length === 0) {
      this.isDialogOpened = true;
      const dialogRef = this.dialog.open(WarningDialog, { width: '50%' });
      dialogRef.componentInstance.confirmMessage =
        'Your session has been expired! Please login again to continue.';
      dialogRef.componentInstance.buttonConfig = [
        new ButtonConfig('Close', false, true, () => dialogRef.close(false))
      ];
      dialogRef.afterClosed().subscribe(() => {
        this.isDialogOpened = false;
        // In case of multiple tabs -> only call logout on first time
        if (localStorage.getItem('needForceLogout') === 'true') {
          this.authService.logout();
        }
        this.sidebarService.setSidebarVisibility(false);
        this.router.navigate(['/']);
        localStorage.setItem('needForceLogout', 'false');
      });
    }
  }

  // Check on each 10s, if there is any activity within 1 min,
  // send heart beat to server to reset counter of server.
  startCounter() {
    this.userActivity = setTimeout(() => {
      this.userInactive.next(undefined);
    }, 60000 / this.INACTIVE_CHECK_NUMBER);
  }

  stopCounter() {
    clearTimeout(this.userActivity);
  }

  hasActivity() {
    localStorage.setItem('lastActivityTime', new Date().getTime().toString());
    this.inactiveCount = 0;

    this.resetCounter();
  }

  resetCounter() {
    this.stopCounter();
    this.startCounter();
  }

  @HostListener('window:mousemove') refreshUserState() {
    this.hasActivity();
  }
  @HostListener('document:keypress') handleKeyboardEvent() {
    this.hasActivity();
  }
  callBack() {}

  ngOnInit() {
    this.adalService.handleWindowCallback(true);
    if (this.commonService.isIE()) {
      const dialogRef = this.dialog.open(WarningDialog, { width: '50%' });
      dialogRef.componentInstance.confirmMessage =
        'IE is not supported. Please use other browser!';

      dialogRef.afterClosed().subscribe((result) => {
        if (!result) {
          window.history.go(-1);
        }
      });
      return;
    }
    this.titleService.setTitle(this.title);
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.getSidebarLinks(currentUser).subscribe(
      (sidebarLinks) => (this.auth.sidebarLinks = sidebarLinks)
    );

    if (this.detectNewWorkingSession()) {
      this.router.navigate(['/']);
    }
  }
  ngAfterViewInit() {
    this.elRef.nativeElement
      .querySelector('bci-burger')
      .addEventListener('click', this.onClick.bind(this));
    if (this.authenticated && window.self === window.top) {
      console.log('Azure Token: ' + this.adalService.userInfo.token);
      this.redirectToSiteMasterPage(this.adalService.userInfo.token);
    }
  }
  get authenticated(): boolean {
    return this.adalService.userInfo.authenticated;
  }
  onClick() {
    this.sidebarService.getSidebarState().subscribe(() => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 500);
    });
  }
  getSidebarLinks(currentUser): Observable<SidebarNavItem[]> {
    // const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (this.commonService.isNullOrUndefined(currentUser)) {
      return this.navigationService.getNavigationItems();
    } else {
      this.authService.userActivities = currentUser.activities;
      this.authService.userRole = currentUser.role.name;

      this.authService.userMenus = currentUser.menuItems;
      const menu = [];
      const sidebarLinksUser: SidebarNavItem[] = this.authService.buildMenus(
        this.authService.defaultMenus,
        menu,
        true
      );

      this.sidebarFooterLinks[0].title =
        currentUser.fullName !== ''
          ? currentUser.fullName
          : currentUser.userName;
      return of(sidebarLinksUser);
    }
  }
  logout() {
    this.adalService.logOut();
    localStorage.removeItem('currentUser');
    this.sidebarService.setSidebarVisibility(false);
    this.router.navigate(['/']);
  }

  onAbout() {
    const dialogRef = this.dialog.open(BciImprintComponent, {
      width: '800px'
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }
  onPolicy() {
    this.dataService
      .getData('./assets/text/privacy.txt', true, true)
      .subscribe((data) => {
        if (data) {
          // Incase can read -> Show policy popup
          const dialogRef = this.dialog.open(SystemPrivacyModalComponent, {
            width: '50%',
            maxHeight: '660px'
          });
          dialogRef.componentInstance.firstLogin = true;
          dialogRef.componentInstance.privacyMessage = data;
          dialogRef.afterClosed().subscribe((result) => {});
        }
      });
  }
  onContact() {
    this.dataService
      .getData('./assets/text/contact.txt', true, true)
      .subscribe((data) => {
        if (data) {
          const dialogRef = this.dialog.open(ContactUsModalComponent, {
            width: '20%',
            maxHeight: '300px'
          });
          dialogRef.componentInstance.privacyMessage = data;
          dialogRef.afterClosed().subscribe((result) => {});
        }
      });
  }
  ngOnDestroy() {}
  detectNewWorkingSession() {
    const loc: any = this.location;
    try {
      if (
        this.commonService.isNullOrUndefined(
          loc._platformStrategy._platformLocation._history.state
        )
      ) {
        return true;
      }
    } catch (err) {
      return false;
    }
  }

  redirectToSiteMasterPage(token) {
    this.authService.getUserDetail().subscribe((userInfo) => {
      if (userInfo !== null) {
        let fullName = '';

        const firstName = !this.commonService.isNullOrUndefined(
          userInfo.data.firstName
        )
          ? userInfo.data.firstName
          : '';
        const lastName = !this.commonService.isNullOrUndefined(
          userInfo.data.lastName
        )
          ? userInfo.data.lastName
          : '';
        if (firstName !== '' && lastName !== '') {
          fullName = firstName + ' ' + lastName;
        }

        const authUser = new AuthUserInfo(
          token,
          '',
          '',
          userInfo.data.username,
          fullName,
          userInfo.data.role,
          userInfo.data.flatMenus,
          0,
          userInfo.data.useSSO,
          userInfo.data.alreadyLoggedIn,
          userInfo.data.activities
        );
        localStorage.setItem('currentUser', JSON.stringify(authUser));
        this.authService.userActivities = userInfo.data.activities;
        this.authService.userRole = userInfo.data.role.name;
        this.authService.userMenus = userInfo.data.flatMenus;
        const menu = [];
        const sidebarLinksUser: SidebarNavItem[] = this.authService.buildMenus(
          this.authService.defaultMenus,
          menu,
          true
        );

        this.auth.sidebarLinks = sidebarLinksUser;
        this.sidebarFooterLinks[0].title =
          authUser.fullName !== '' ? authUser.fullName : authUser.userName;
        console.log('authUser.alreadyLoggedIn --> ' + authUser.alreadyLoggedIn);
        if (!authUser.alreadyLoggedIn) {
          if (this.dialog.openDialogs.length === 0) {
            this.dataService
              .getData('./assets/text/privacy.txt', true, true)
              .subscribe((data) => {
                if (data) {
                  // Incase can read -> Show policy popup
                  const dialogRef = this.dialog.open(
                    SystemPrivacyModalComponent,
                    {
                      width: '50%',
                      maxHeight: '660px'
                    }
                  );
                  dialogRef.componentInstance.privacyMessage = data;
                  dialogRef.afterClosed().subscribe((data) => {
                    if (data && data.data) {
                      this.authService
                        .takeOutFirstTimeOfUser()
                        .subscribe((data) => {
                          // console.log(data);
                        });
                      this.navigate();
                    }
                  });
                } else {
                  // Incase cannot read privacy file -> Navigate
                  this.navigate();
                }
              });
          }
        } else {
          // In case: not the first time user login
          this.navigate();
        }
      }
    });
  }

  navigate() {
    // Store time for checking inactive user
    localStorage.setItem('lastActivityTime', new Date().getTime().toString());
    localStorage.setItem('needForceLogout', 'false');
    //
    this.siteService.getSiteDetails().subscribe((sites) => {
      if (sites) {
        if (sites.length > 1) {
          this.router.navigate(['/site-master']);
          localStorage.setItem('isMultipleSites', 'true');
        } else if (sites.length === 1) {
          const find = this.auth.sidebarLinks.find(
            (c: any) => c.code === 'HOME'
          );
          if (sites[0].siteId === 'TATAThen625564') {
            const helpIndex = this.auth.sidebarLinks.findIndex(
              (link: any) => link.code === 'HELP'
            );
            if (helpIndex) {
              this.auth.sidebarLinks.splice(helpIndex, 1);
            }
          }
          if (!this.commonService.isNullOrUndefined(find)) {
            find.url = 'site-master';
            localStorage.setItem('siteId', sites[0].siteId);
            localStorage.setItem('isMultipleSites', 'false');
          }
          this.router.navigate(['site', 'over-view'], {
            queryParams: { siteId: sites[0].siteId }
          });
        }
      }
    });
  }
}
